import { Injectable, inject } from '@angular/core';
import { ABP, LocalizationService, RoutesService, TreeNode } from '@abp/ng.core';
import { MOBILE_NAVBAR_ITEMS_FILTER_TOKEN } from '@volo/abp.ng.lepton-x.core';
import { LpxNavbarItem, NavbarService } from '@volo/ngx-lepton-x.core';
import { map } from 'rxjs';
import { NAVIGABLE_PARENT_ROUTE_NAMES } from 'src/core/constants/theme/navigable-parent-routes.constants';

/**
 * Feeds the side menu from ABP's routes, replacing `AbpNavbarService` of `@volo/abp.ng.lepton-x.core` 5.3.0.
 *
 * It is that service line for line except for one decision: upstream drops the link of every route that
 * has children, so a parent can only expand and never open a page. Here a parent listed in
 * {@link NAVIGABLE_PARENT_ROUTE_NAMES} keeps its link, and LeptonX's menu item — an anchor carrying both
 * `routerLink` and an expand-on-click handler — then opens the page and expands the group in one click.
 *
 * ⚠️ When LeptonX is upgraded, re-diff this against the upstream `AbpNavbarService`.
 */
@Injectable()
export class AcxNavbarService {
  private readonly routes = inject(RoutesService);
  private readonly navbarService = inject(NavbarService);
  private readonly localizationService = inject(LocalizationService);
  private readonly mobileMenuItemFilterFn = inject(MOBILE_NAVBAR_ITEMS_FILTER_TOKEN);

  initRoutes(): void {
    this.routes.visible$
      .pipe(map(routes => routes.filter(route => route.path || route.children.length)))
      .subscribe(routes => {
        this.navbarService.setNavbarItems(...routes.map(this.mapRouteToNavItem));
      });
  }

  private readonly mapRouteToNavItem = (route: TreeNode<ABP.Route>, index: number): LpxNavbarItem => {
    const navbarItem: LpxNavbarItem = {
      text: this.localizationService.instant(route.name),
      link: this.resolveLink(route),
      icon: route.iconClass,
      children: route.children.map(this.mapRouteToNavItem),
      showOnMobileNavbar: this.mobileMenuItemFilterFn(route, index),
      group: route.group,
    };
    if (route.breadcrumbText) {
      navbarItem.breadcrumbText = this.localizationService.instant(route.breadcrumbText);
    }
    return navbarItem;
  };

  private resolveLink(route: TreeNode<ABP.Route>): string | undefined {
    const hasChildren = route.children.length > 0;
    return hasChildren && !NAVIGABLE_PARENT_ROUTE_NAMES.has(route.name) ? undefined : route.path;
  }
}
